import type { UserId } from '@solinkcorp/api-types';
import settings from '@solinkcorp/settings';
import CallhomeApi from 'clients/CallhomeApi';
import { firebaseApp, VAPID_KEY } from 'configs/firebase.config';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { useEffect, useRef } from 'react';

export type FcmNotification = {
  body: string;
  eventId: string;
  id: string;
  launchURL: string;
  subtitle: string;
  title: string;
  type: string;
  timestamp: number;
};

/**
 * Manages the full FCM push notification lifecycle for a signed-in web user.
 *
 * Mirrors apps/mobile/features/pushNotifications/usePushNotifications.ts.
 * Registers the FCM token with CallHome on mount / userId change,
 * and tears down both the Firebase subscription and the server-side binding
 * on unmount or when userId changes (logout / account switch).
 *
 * Pass `userId` as `undefined` to skip registration entirely (e.g. guest
 * users, Safari, kiosk mode).
 */
export function usePushNotificationLifecycle(
  userId: UserId | undefined,
  jwtToken: string | undefined,
  onNotification: (notification: FcmNotification) => void,
): void {
  // Map of userId -> latest JWT. Reading jwtByUserIdRef.current[id] in any closure
  // always yields the most recent token for that user, even after a mid-session
  // refresh.
  const jwtByUserIdRef = useRef<Record<string, string>>({});
  if (userId && jwtToken) jwtByUserIdRef.current[userId] = jwtToken;

  const onNotificationRef = useRef(onNotification);
  onNotificationRef.current = onNotification;

  useEffect(() => {
    if (!userId || !jwtByUserIdRef.current[userId]) return;

    const messaging = getMessaging(firebaseApp);
    let fcmToken: string | null = null;
    let cancelled = false;

    getToken(messaging, { vapidKey: VAPID_KEY })
      .then((token) => {
        if (!token || cancelled) return;
        const jwt = jwtByUserIdRef.current[userId];
        if (!jwt) return;
        fcmToken = token;
        return CallhomeApi(jwt).pushNotifications(userId, token);
      })
      .catch((err) => console.error('[PushNotifications] Registration failed', err));

    const unsubMessage = onMessage(messaging, (payload) => {
      const { data } = payload;
      if (data) {
        onNotificationRef.current({ ...data, timestamp: Date.now() } as FcmNotification);
      }
    });

    // Keepalive DELETE for tab-close: Axios is cancelled on unload, but a fetch
    // with keepalive:true survives. The beforeunload listener is removed on normal
    // cleanup so only one of the two paths fires.
    const handleBeforeUnload = () => {
      if (!fcmToken) return;
      const jwt = jwtByUserIdRef.current[userId];
      if (!jwt) return;
      const url = `${settings.callhome_url}/api/users/${encodeURIComponent(userId)}/pushNotifications/${encodeURIComponent(fcmToken)}`;
      fetch(url, {
        method: 'DELETE',
        keepalive: true,
        headers: { Authorization: `Bearer ${jwt}` },
      }).catch(() => {});
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      cancelled = true;
      unsubMessage();

      handleBeforeUnload();
    };
  }, [userId]);
}
